import { useContext } from 'react'
import { compact, includes, map } from 'lodash'

import { CreateExperimentBlockedReason, User, UserRole } from '../api/models'
import { AppStateContext } from '../state/AppProvider'

export interface CreateExperimentBlock {
  blocked: boolean
  reason: CreateExperimentBlockedReason | null
}

/**
 * Derives whether the current user is blocked from creating experiments and why.
 *
 * The structured `create_experiment_blocked_reason` (Story 6.1) is the source of truth.
 * When it is absent (e.g. backend not yet deployed), we fall back to the legacy proxy
 * (VIEWER role) so behavior does not regress during partial rollouts.
 */
export const getCreateExperimentBlock = (user: User | null | undefined): CreateExperimentBlock => {
  if (user?.create_experiment_blocked_reason) {
    return { blocked: true, reason: user.create_experiment_blocked_reason }
  }
  return { blocked: user?.role === UserRole.VIEWER, reason: null }
}

export const hasPermission = (requiredPermission: string): boolean => {
  const appContext = useContext(AppStateContext)

  return includes(appContext?.state.permissions, requiredPermission)
}

export const extractTruePermissions = (permissions: Record<string, boolean>): string[] => {
  return compact(map(permissions, (value, key) => (value === true ? key : null)))
}

export const getRoleLabel = (role: string) => {
  switch (role) {
    case UserRole.ADMIN:
      return 'Admin'
    case UserRole.DECODER:
      return 'Decoder'
    case UserRole.OU_ORCHESTRATOR:
      return 'OU Orchestrator'
    case UserRole.EDITOR:
      return 'Editor'
    case UserRole.VIEWER:
      return 'Viewer'
  }
}
